/**
 * AccountMenu.tsx
 *
 * A single unified account dropdown for the Navbar.
 * Renders the correct menu based on the user's role:
 *   - isPartner → PartnerAccountMenu
 *   - isSupplier  → SupplierAccountMenu
 *   - otherwise   → TravelerAccountMenu
 *
 * Usage in Navbar:
 *   <AccountMenu />
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@mondivago/supabase';
import { useTranslation } from 'react-i18next';
import { LogOut, ChevronDown, ChevronRight, UserRound, X } from 'lucide-react';
import { ActionModal } from '@/components/ui/Modal';
import { useSupplierProfile } from '@/hooks/suppliers';
import { useTravelerProfile } from '@/hooks/traveler';
import { buildSupplierPath } from '@/shared/seo/seoRouting';
import { getSupabaseImageUrl } from '@/shared/lib/image';
import { BookMarked, Heart, MessageSquare, Settings, Home, Calendar, type LucideIcon } from 'lucide-react';

// ─── Traveler dropdown items ───

const TRAVELER_PUBLIC_DROPDOWN_ITEMS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/trips/bookings', label: 'My Trips', icon: BookMarked },
  { to: '/trips/wishlist', label: 'Wishlist', icon: Heart },
  { to: '/trips/messages', label: 'Messages', icon: MessageSquare },
  { to: '/trips/settings', label: 'Settings', icon: Settings },
];

// ─── Supplier dropdown items (subset of nav items) ───

interface SupplierDropdownItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const SUPPLIER_PUBLIC_DROPDOWN_ITEMS: SupplierDropdownItem[] = [
  { to: '/supplier/dashboard', label: 'Dashboard', icon: Home },
  { to: '/supplier/calendar', label: 'Calendar', icon: Calendar },
  { to: '/supplier/bookings', label: 'Bookings', icon: BookMarked },
  { to: '/supplier/account-settings', label: 'Account Settings', icon: Settings },
];

// ─── Shared sub-components ────────────────────────────────────────────────────

interface DropdownShellProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  header: React.ReactNode;
  menuItems: { to: string; label: string; icon: React.ElementType }[];
  onLogout: () => void;
  logoutConfirmOpen: boolean;
  onLogoutConfirmClose: () => void;
  onLogoutConfirm: () => void;
  logoutDescription?: string;
}

function DropdownShell({
  isOpen,
  onClose,
  trigger,
  header,
  menuItems,
  onLogout,
  logoutConfirmOpen,
  onLogoutConfirmClose,
  onLogoutConfirm,
  logoutDescription = 'Are you sure you want to log out?',
}: DropdownShellProps) {
  const { t } = useTranslation();

  return (
    <div className="relative">
      {trigger}

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={onClose} />

          <div className="absolute right-0 top-full z-[90] mt-2 w-[280px] max-w-[96vw] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06]">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div className="flex items-center gap-3">{header}</div>
                <button
                  onClick={onClose}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Menu items */}
              <div className="px-3 py-3">
                <div className="space-y-1">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={onClose}
                        className="group flex items-center justify-between rounded-xl px-3 py-2.5 transition-all hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-900" />
                          <span className="nav-label !text-gray-700 transition-colors group-hover:text-gray-900">
                            {item.label}
                          </span>
                        </div>
                        <ChevronRight className="h-3.5 w-3.5 text-gray-300 opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-1" />
                      </Link>
                    );
                  })}
                </div>

                <div className="my-2 border-t border-gray-100" />

                {/* Logout */}
                <button
                  type="button"
                  onClick={onLogout}
                  className="group flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all hover:bg-rose-50/50"
                >
                  <LogOut className="h-4 w-4 text-gray-400 transition-colors group-hover:text-rose-600" />
                  <span className="nav-label !text-gray-700 transition-colors group-hover:text-rose-600">
                    {t('nav.logout', 'Logout')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ActionModal
        isOpen={logoutConfirmOpen}
        onClose={onLogoutConfirmClose}
        title={t('nav.logoutConfirmTitle', 'Confirm logout?')}
        description={logoutDescription}
        actionLabel={t('nav.logout', 'Logout')}
        onAction={onLogoutConfirm}
        actionVariant="danger"
      />
    </div>
  );
}

// ─── Avatar trigger button ────────────────────────────────────────────────────

interface AvatarTriggerProps {
  photo: string | null;
  name: string;
  isOpen: boolean;
  onClick: () => void;
  fallbackIcon?: React.ElementType;
  fallbackBg?: string;
  badge?: string | null;
  isParentAdmin?: boolean;
}

function AvatarTrigger({
  photo,
  name,
  isOpen,
  onClick,
  fallbackIcon: FallbackIcon = UserRound,
  fallbackBg = 'bg-slate-200 text-slate-500',
  badge,
  isParentAdmin,
}: AvatarTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 nav-label text-slate-950 transition-all hover:bg-gray-50 hover:border-gray-300 active:scale-95 shadow-sm group"
      aria-label="Open account menu"
      aria-expanded={isOpen}
    >
      <div className="relative h-6 w-6 shrink-0 overflow-visible rounded-full transition-all">
        <div className="h-full w-full overflow-hidden rounded-full">
          {photo ? (
            <img
              loading="lazy"
              src={getSupabaseImageUrl(photo, 'public', { width: 64, height: 64, format: 'webp' })}
              alt="Profile"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className={`flex h-full w-full items-center justify-center ${fallbackBg}`}>
              <FallbackIcon className="h-4 w-4" />
            </div>
          )}
        </div>
        {badge && (
          <div
            className={`absolute -bottom-1 ${isParentAdmin ? '-right-1' : '-left-1'} flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 shadow-sm ring-1 ring-black/5`}
          >
            <img
              src={getSupabaseImageUrl(badge, 'public', { width: 32, height: 32, format: 'webp' })}
              alt=""
              className="h-full w-full object-cover rounded-full"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 overflow-hidden">
        <span className="truncate text-xs font-black tracking-tight max-w-[100px] md:max-w-[140px]">
          {name.split(' ')[0]}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </div>
    </button>
  );
}

// ─── Dropdown header ──────────────────────────────────────────────────────────

function DropdownHeader({
  photo,
  name,
  caption,
  fallbackIcon: FallbackIcon = UserRound,
  badge,
  isParentAdmin,
}: {
  photo: string | null;
  name: string;
  caption?: string | null;
  fallbackIcon?: React.ElementType;
  badge?: string | null;
  isParentAdmin?: boolean;
}) {
  return (
    <>
      <div className="relative h-8 w-8 shrink-0 overflow-visible">
        <div className="h-full w-full overflow-hidden rounded-full border border-gray-200">
          {photo ? (
            <img
              src={getSupabaseImageUrl(photo, 'public', { width: 64, height: 64, format: 'webp' })}
              alt={name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-400">
              <FallbackIcon className="h-5 w-5" />
            </div>
          )}
        </div>
        {badge && (
          <div
            className={`absolute -bottom-1 ${isParentAdmin ? '-right-1' : '-left-1'} flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 shadow-md ring-1 ring-black/5`}
          >
            <img
              src={getSupabaseImageUrl(badge, 'public', { width: 32, height: 32, format: 'webp' })}
              alt=""
              className="h-full w-full object-cover rounded-full"
            />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate nav-label text-gray-900">{name}</p>
        {caption && <p className="truncate text-[11px] font-medium text-gray-400 leading-tight mt-0.5">{caption}</p>}
      </div>
    </>
  );
}

// ─── Unified Account Menu (Handles state & UI) ────────────────────────────────

interface UnifiedMenuProps {
  photo: string | null;
  name: string;
  caption?: string | null;
  menuItems: { to: string; label: string; icon: React.ElementType }[];
  badge?: string | null;
  fallbackIcon?: React.ElementType;
  fallbackBg?: string;
  isParentAdmin?: boolean;
  logoutDescription?: string;
}

function UnifiedAccountMenu({
  photo,
  name,
  caption,
  menuItems,
  badge,
  fallbackIcon = UserRound,
  fallbackBg,
  isParentAdmin,
  logoutDescription,
}: UnifiedMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    setIsOpen(false);
    setLogoutOpen(false);
    void navigate('/', { replace: true });
  };

  return (
    <DropdownShell
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      trigger={
        <AvatarTrigger
          photo={photo}
          name={name}
          isOpen={isOpen}
          onClick={() => {
            setIsOpen((p) => !p);
          }}
          badge={badge}
          isParentAdmin={isParentAdmin}
          fallbackIcon={fallbackIcon}
          fallbackBg={fallbackBg}
        />
      }
      header={
        <DropdownHeader
          photo={photo}
          name={name}
          caption={caption}
          badge={badge}
          isParentAdmin={isParentAdmin}
          fallbackIcon={fallbackIcon}
        />
      }
      menuItems={menuItems}
      onLogout={() => {
        setLogoutOpen(true);
      }}
      logoutConfirmOpen={logoutOpen}
      onLogoutConfirmClose={() => {
        setLogoutOpen(false);
      }}
      onLogoutConfirm={handleLogout}
      logoutDescription={logoutDescription}
    />
  );
}

// ─── Supplier menu ───────────────────────────────────────────────────────────

function SupplierMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, agency, role: agencyRole } = useSupplierProfile(); // agencyRole = supplier company role

  const metadata = user?.user_metadata;
  const defaultPhoto =
    (metadata?.avatar_url as string | undefined) ?? (metadata?.picture as string | undefined) ?? null;
  const defaultName =
    (metadata?.full_name as string | undefined) ?? (metadata?.name as string | undefined) ?? 'Supplier';

  const isParentAdmin = agencyRole === 'agency' && !!agency?.photo_url;
  const photo = isParentAdmin ? agency.photo_url! : (profile?.photo_url ?? defaultPhoto);
  const badge = isParentAdmin ? (profile?.photo_url ?? defaultPhoto) : (agency?.photo_url ?? null);
  const name = isParentAdmin
    ? (agency?.company_name ?? defaultName)
    : profile
      ? profile.company_name || defaultName
      : defaultName;

  const caption = isParentAdmin
    ? (agency?.company_name ?? 'Agency Account')
    : agency?.company_name
      ? agency.company_name
      : profile?.company_name
        ? profile.type === 'agency'
          ? 'Agency Account'
          : 'Individual Supplier'
        : 'Supplier Account';

  const profileLink = agency?.short_id
    ? buildSupplierPath(agency.company_name, agency.short_id)
    : profile
      ? buildSupplierPath(profile.company_name, profile.short_id)
      : '#';

  const menuItems = [
    {
      to: profileLink,
      label: agency?.id ? t('nav.agencyProfile', 'Parent Profile') : t('nav.myProfile', 'My profile'),
      icon: UserRound,
    },
    ...SUPPLIER_PUBLIC_DROPDOWN_ITEMS.map((item) => {
      let translatedLabel = item.label;
      if (item.label === 'Dashboard') translatedLabel = t('nav.dashboard', 'Dashboard');
      if (item.label === 'Calendar') translatedLabel = t('nav.calendar', 'Calendar');
      if (item.label === 'Bookings') translatedLabel = t('nav.bookings', 'Bookings');
      if (item.label === 'Account Settings') translatedLabel = t('nav.accountSettings', 'Account Settings');
      return { ...item, label: translatedLabel };
    }),
  ];

  return (
    <UnifiedAccountMenu
      photo={photo}
      name={name}
      caption={caption}
      menuItems={menuItems}
      badge={badge}
      isParentAdmin={isParentAdmin}
      logoutDescription={t(
        'nav.logoutDescSupplier',
        'Are you sure you want to log out? You will need to log in again to manage your tours.'
      )}
    />
  );
}

// ─── Traveler menu ────────────────────────────────────────────────────────────

function TravelerMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useTravelerProfile();
  const [photo, setPhoto] = useState<string | null>(null);
  const [name, setName] = useState('Traveler');

  useEffect(() => {
    if (profile) {
      if (profile.first_name || profile.last_name) {
        setName(`${profile.first_name || ''} ${profile.last_name || ''}`.trim());
      }
      if (profile.photo_url) setPhoto(profile.photo_url);
    } else if (user) {
      setName((user.user_metadata.full_name as string) || (user.user_metadata.name as string) || 'Traveler');
      setPhoto((user.user_metadata.avatar_url as string) || (user.user_metadata.picture as string) || null);
    }
  }, [profile, user]);

  const menuItems = TRAVELER_PUBLIC_DROPDOWN_ITEMS.map((item) => {
    let translatedLabel = item.label;
    if (item.label === 'My Trips') translatedLabel = t('nav.myTrips', 'My Trips');
    if (item.label === 'Wishlist') translatedLabel = t('nav.wishlist', 'Wishlist');
    if (item.label === 'Messages') translatedLabel = t('nav.messages', 'Messages');
    if (item.label === 'Settings') translatedLabel = t('nav.settings', 'Settings');
    return { ...item, label: translatedLabel };
  });

  return <UnifiedAccountMenu photo={photo} name={name} caption="Traveler Account" menuItems={menuItems} />;
}

// ─── Partner menu (single item: Partner Dashboard) ─────────────────────────

function PartnerMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const metadata = user?.user_metadata;
  const photo = (metadata?.avatar_url as string | undefined) ?? (metadata?.picture as string | undefined) ?? null;
  const name = (metadata?.full_name as string | undefined) ?? (metadata?.name as string | undefined) ?? 'Partner';

  const menuItems = [
    {
      to: '/partner',
      label: t('nav.partnerDashboard', 'Partner Dashboard'),
      icon: UserRound,
    },
  ];

  return (
    <UnifiedAccountMenu
      photo={photo}
      name={name}
      caption="Partner Account"
      menuItems={menuItems}
      logoutDescription={t(
        'nav.logoutDescPartner',
        'Are you sure you want to log out? You will need to log in again to access your partner portal.'
      )}
    />
  );
}

// ─── Main export — auto-selects the right menu ────────────────────────────────

interface AccountMenuProps {
  isSupplier: boolean;
  isPartner?: boolean;
}

export function AccountMenu({ isSupplier, isPartner }: AccountMenuProps) {
  if (isSupplier) return <SupplierMenu />;
  if (isPartner) return <PartnerMenu />;
  return <TravelerMenu />;
}
